import React, { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './DetachablePanel.css';
import { copyStyles } from '../utils/popoutWindow';
import { attachDocument as attachZoomDocument } from '../utils/appZoom';

const DetachablePanel = ({ title, children, isDetached, onDetach, onAttach, defaultSize }) => {
  const popupRef = useRef(null);
  const checkIntervalRef = useRef(null);
  // 우리가 의도적으로 popup 을 닫는 경우 (StrictMode cleanup, isDetached=false 등)
  // beforeunload/polling 이 firing 되어 onAttach 가 호출되는 것을 방지하는 가드.
  // user 가 직접 popup 의 X 를 누른 경우엔 false 이므로 정상 onAttach.
  const programmaticCloseRef = useRef(false);
  const [portalContainer, setPortalContainer] = useState(null);

  const copyStylesToPopup = useCallback((popup) => {
    // 부모 스타일 복사는 팝업 창 「새 창으로」 와 같은 함수(utils/popoutWindow) — 두 벌이면 한쪽만 스타일이 어긋난다.
    // 열린 뒤 붙는 스타일도 따라간다(창이 닫히면 스스로 멈춘다)
    copyStyles(popup);

    const baseStyle = popup.document.createElement('style');
    baseStyle.textContent = `
      html, body { margin: 0; padding: 0; width: 100%; height: 100%; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; overflow: hidden; }
      #detach-root { display: flex; flex-direction: column; height: 100%; width: 100%; overflow: hidden; }
      .cm-editor { height: 100% !important; font-family: Consolas, 'Courier New', monospace !important; }
      .cm-scroller { overflow: auto !important; }
      .cm-content { font-family: Consolas, 'Courier New', monospace !important; }
      .cm-line { font-family: Consolas, 'Courier New', monospace !important; }
      .cm-gutters { font-family: Consolas, 'Courier New', monospace !important; }
      .codemirror-editor-area { flex: 1 !important; display: flex !important; flex-direction: column !important; }
      .sql-editor-pane { display: flex; flex-direction: column; overflow: hidden; }
      .file-tabs-container { flex-shrink: 0; }
      .result-and-pagination-area { display: flex; flex-direction: column; height: 100%; }

      /* 다크모드 — 팝업 창 내부 */
      [data-theme="dark"] body { background: #1e1e1e; color: #d4d4d4; }
      [data-theme="dark"] .detach-inline-btn { color: #aaa; border-color: #404040; }
      [data-theme="dark"] .detach-inline-btn:hover { color: #007acc; border-color: #007acc; }
      [data-theme="dark"] .sql-editor-pane { background: #1e1e1e; }
      [data-theme="dark"] .result-and-pagination-area { background: #1e1e1e; }
      [data-theme="dark"] .file-tabs-container { background: #2d2d2d; }
      [data-theme="dark"] .editor-search-bar { background: #2d2d2d; border-color: #404040; }
      [data-theme="dark"] .editor-search-input { color: #d4d4d4; }
      [data-theme="dark"] .editor-search-nav { color: #aaa; }
      [data-theme="dark"] .editor-search-nav:hover { background: #37373d; }
      [data-theme="dark"] .editor-option-label { color: #aaa; }
      [data-theme="dark"] .cm-editor { background: #1e1e1e !important; }
      [data-theme="dark"] .cm-gutters { background: #252526 !important; border-color: #404040 !important; color: #777 !important; }
      [data-theme="dark"] .cm-content { color: #d4d4d4 !important; }
      [data-theme="dark"] .cm-activeLine { background: rgba(255,255,255,0.04) !important; }
      [data-theme="dark"] .cm-activeLineGutter { background: #264f78 !important; color: #fff !important; }
      [data-theme="dark"] .cm-cursor { border-color: #d4d4d4 !important; }
      [data-theme="dark"] .sql-editor-minimap { background: rgba(30,30,30,0.85) !important; border-color: #404040 !important; }
    `;
    popup.document.head.appendChild(baseStyle);
  }, []);

  // 팝업 닫힘 시 정리 (포탈 먼저 제거 → React 언마운트 → onAttach)
  const handlePopupClosed = useCallback(() => {
    // 우리가 의도적으로 닫은 경우 (cleanup) — onAttach 호출하지 않음.
    // 그렇지 않으면 StrictMode dev double-effect 로 popup 이 열렸다가 즉시 사라지는 버그.
    if (programmaticCloseRef.current) {
      programmaticCloseRef.current = false;
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      popupRef.current = null;
      return;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    popupRef.current = null;
    // 포탈 컨테이너 먼저 제거 (React 컴포넌트 언마운트 → ResizeObserver 정리)
    setPortalContainer(null);
    // 다음 프레임에서 onAttach 호출 (언마운트 완료 후)
    requestAnimationFrame(() => onAttach());
  }, [onAttach]);

  const openPopup = useCallback(() => {
    const w = defaultSize?.width || 1000;
    const h = defaultSize?.height || 700;
    const left = window.screenX + 80;
    const top = window.screenY + 60;

    const popup = window.open(
      '',
      `livesql_${title.replace(/\s/g, '_')}_${Date.now()}`,
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      console.warn('팝업이 차단되었습니다. 팝업 허용 후 다시 시도하세요.');
      onAttach();
      return;
    }

    popupRef.current = popup;

    // 초기 HTML 작성
    const theme = document.documentElement.getAttribute('data-theme') || 'light';
    popup.document.open();
    popup.document.write(`<!DOCTYPE html><html data-theme="${theme}"><head><title>LiveSQL - ${title}</title></head><body style="margin:0;padding:0;overflow:hidden;"><div id="detach-root" style="display:flex;flex-direction:column;height:100%;width:100%;overflow:hidden;"></div></body></html>`);
    popup.document.close();

    const setupPopup = () => {
      if (!popupRef.current || popupRef.current.closed) return;

      copyStylesToPopup(popup);

      const container = popup.document.getElementById('detach-root');
      if (!container) return;

      const activate = () => {
        if (!popupRef.current || popupRef.current.closed) return;
        setPortalContainer(container);
        setTimeout(() => {
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.dispatchEvent(new Event('resize'));
          }
        }, 200);
      };

      if (popup.document.fonts && popup.document.fonts.ready) {
        popup.document.fonts.ready.then(() => setTimeout(activate, 50));
      } else {
        setTimeout(activate, 300);
      }

      // 부모 다크모드 변경 시 팝업에도 반영
      const themeObserver = new MutationObserver(() => {
        if (popup && !popup.closed) {
          const t = document.documentElement.getAttribute('data-theme');
          popup.document.documentElement.setAttribute('data-theme', t || 'light');
        }
      });
      themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      // 화면 확대/축소 — 이 창에서도 Ctrl+휠·단축키가 앱 배율을 바꾼다(utils/appZoom, 배율은 main 이 이 창에도 건다)
      const undoZoom = attachZoomDocument(popup.document);

      // 팝업에서 beforeunload로 정리
      popup.addEventListener('beforeunload', () => {
        themeObserver.disconnect();
        undoZoom();
        handlePopupClosed();
      });

      // 폴백: 주기적 닫힘 감지
      checkIntervalRef.current = setInterval(() => {
        if (!popup || popup.closed) {
          handlePopupClosed();
        }
      }, 500);

      popup.addEventListener('keydown', (e) => {
        if (e.ctrlKey && e.key === 's') e.preventDefault();
      });
    };

    const checkReady = () => {
      if (popup.closed) return;
      try {
        if (popup.document.getElementById('detach-root')) {
          setupPopup();
        } else {
          setTimeout(checkReady, 50);
        }
      } catch (e) {
        setTimeout(checkReady, 50);
      }
    };
    checkReady();
  }, [title, defaultSize, onAttach, copyStylesToPopup, handlePopupClosed]);

  useEffect(() => {
    if (isDetached) {
      openPopup();
    } else {
      // 명시적 결합 시 정리 — programmatic 플래그로 onAttach 재호출 방지
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (popupRef.current && !popupRef.current.closed) {
        programmaticCloseRef.current = true;
        setPortalContainer(null);
        popupRef.current.close();
      }
      popupRef.current = null;
    }

    return () => {
      // StrictMode dev 의 double-effect 또는 unmount cleanup —
      // popup 닫을 때 onAttach 가 다시 불리지 않도록 플래그 설정.
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (popupRef.current && !popupRef.current.closed) {
        programmaticCloseRef.current = true;
        setPortalContainer(null);
        popupRef.current.close();
      }
    };
  }, [isDetached]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isDetached || !portalContainer) return null;

  return createPortal(children, portalContainer);
};

export default DetachablePanel;
